import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "antd";
import { AlertCircle, Loader2 } from "lucide-react";
import { mfaApi } from "../../../api/mfa";
import { completeLogin } from "../../../utils/auth";

const CHALLENGE_TOKEN_KEY = "mfaChallengeToken";

const MfaChallengeForm: React.FC = () => {
  const navigate = useNavigate();
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    const token = sessionStorage.getItem(CHALLENGE_TOKEN_KEY);
    if (!token) {
      navigate("/auth/login");
      return;
    }
    setChallengeToken(token);
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challengeToken || !code) return;

    setIsLoading(true);
    setServerError(null);

    try {
      const response = await mfaApi.verifyChallenge(challengeToken, code);
      sessionStorage.removeItem(CHALLENGE_TOKEN_KEY);
      completeLogin(response.data.accessToken, navigate);
    } catch (error: any) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;

      // "Incorrect code" is the only failure that should keep the user on
      // this screen - everything else (expired/invalid challenge token,
      // MFA no longer enabled, rate-limit lockout) means this challenge is
      // dead and they need to log in again. Matches the exact detail
      // string auth_controller.py's verify_mfa_challenge raises for a
      // wrong code - keep these in sync if either side changes.
      if (status === 401 && detail === "Incorrect code") {
        setServerError("Incorrect code. Please try again.");
        setIsLoading(false);
        return;
      }

      sessionStorage.removeItem(CHALLENGE_TOKEN_KEY);
      if (status === 429) {
        navigate("/auth/login?logout=true", {
          state: { message: "Too many attempts. Please log in again." },
        });
      } else {
        navigate("/auth/login?logout=true", {
          state: { message: "Your session expired. Please log in again." },
        });
      }
    }
  };

  if (!challengeToken) {
    // Redirect effect above is already navigating away.
    return null;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {serverError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3 animate-shake">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">{serverError}</p>
          </div>
        </div>
      )}

      {useBackupCode ? (
        <div>
          <label htmlFor="backupCode" className="block text-sm font-medium text-gray-700 mb-2">
            Backup code
          </label>
          <input
            id="backupCode"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="XXXX-XXXX"
            autoComplete="one-time-code"
            className="block w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all disabled:bg-gray-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          />
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Authenticator code
          </label>
          <div className="flex justify-center">
            <Input.OTP length={6} value={code} onChange={setCode} disabled={isLoading} />
          </div>
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading || !code}
        className={`
          w-full flex items-center justify-center gap-2 py-3 px-4 border border-transparent rounded-lg
          text-white font-medium text-sm
          transition-all duration-200
          ${
            isLoading || !code
              ? "bg-blue-400 cursor-not-allowed"
              : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 active:scale-[0.98] shadow-lg hover:shadow-xl"
          }
        `}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Verifying...
          </>
        ) : (
          "Verify"
        )}
      </button>

      <button
        type="button"
        onClick={() => {
          setUseBackupCode(!useBackupCode);
          setCode("");
          setServerError(null);
        }}
        className="w-full text-sm text-blue-600 hover:text-blue-800 transition-colors"
      >
        {useBackupCode ? "Use authenticator code instead" : "Use a backup code instead"}
      </button>
    </form>
  );
};

export default MfaChallengeForm;
